import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Plus, Map, PenLine, CalendarDays } from "lucide-react";

/** Pages where the Create FAB should not appear */
const HIDDEN_PATHS = ["/create-trip", "/create-entity"];

interface Action {
  label: string;
  emoji: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
}

export const CreateFAB = React.memo(function CreateFAB() {
  const [isOpen, setIsOpen] = useState(false);
  const [location, setLocation] = useLocation();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const shouldHide = HIDDEN_PATHS.some((p) => location.startsWith(p));

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (
        isOpen &&
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    },
    [isOpen],
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  // Close when navigating
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const actions: Action[] = [
    {
      label: t("createFab.planTrip", "Plan a Trip"),
      emoji: "🗺️",
      icon: Map,
      path: "/create-trip",
    },
    {
      label: t("createFab.travelCalendar", "Travel Calendar"),
      emoji: "📅",
      icon: CalendarDays,
      path: "/travel-calendar",
    },
    {
      label: t("createFab.createEntity", "Create Event or Route"),
      emoji: "✏️",
      icon: PenLine,
      path: "/create-entity",
    },
  ];

  if (shouldHide) return null;

  return (
    <div
      ref={containerRef}
      className="fixed bottom-20 right-4 z-30 flex flex-col items-end gap-2 md:hidden"
    >
      {/* Speed-dial action cards — stagger in from bottom */}
      {isOpen &&
        actions.map((action, index) => (
          <button
            key={action.path}
            onClick={() => {
              setIsOpen(false);
              setLocation(action.path);
            }}
            className="flex items-center gap-2 rounded-full pl-3 pr-4 py-2.5 text-white shadow-lg"
            style={{
              background:
                "linear-gradient(135deg, hsl(var(--brand-navy)), hsl(var(--brand-navy) / 0.85))",
              border: "1px solid hsl(var(--accent-app) / 0.3)",
              animation: `fabSlideIn ${150 + index * 60}ms cubic-bezier(0.16, 1, 0.3, 1) both`,
            }}
            aria-label={action.label}
          >
            <span aria-hidden="true" className="text-base leading-none">
              {action.emoji}
            </span>
            <span className="text-sm font-medium whitespace-nowrap">
              {action.label}
            </span>
          </button>
        ))}

      {/* Main FAB button */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg transition-transform duration-200 hover:scale-105 active:scale-95"
        style={{
          background:
            "linear-gradient(135deg, hsl(var(--brand-coral)), hsl(var(--brand-coral) / 0.8))",
          boxShadow:
            "0 4px 20px hsl(var(--brand-coral) / 0.4), 0 2px 8px hsl(var(--foreground) / 0.2)",
          transition: "transform 200ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
        aria-label={t("createFab.toggle", "Create")}
        aria-expanded={isOpen}
      >
        <Plus
          className="h-6 w-6"
          aria-hidden="true"
          style={{
            transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
            transition: "transform 200ms cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        />
      </button>

      <style>{`
        @keyframes fabSlideIn {
          from {
            opacity: 0;
            transform: translateY(12px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
});
